import $ from 'jquery';
import get from 'lodash/get';
import { morphism } from 'morphism';
import uuid from 'uuid/v4';

import {
  INTEGRATION_TYPE,
  PLATFORM_TYPE,
  PROCESSOR,
  TERMINAL_EVENT,
  TERMINAL_MODEL,
  TerminalEventMonitoring as LspayTerminalMonitoring,
} from '@lightspeed-payments/payments-terminal-sdk';
import { Product } from '@lightspeed-payments/utils';
import CommonData from 'app/src/data/CommonData';
import CCTerminal from 'app/src/data/models/CCTerminal';
import Payment from 'app/src/data/models/Payment';
import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import SalePayment from 'app/src/data/models/SalePayment';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import Terminal, { TerminalTransactionResponse } from 'app/src/payments/terminals/Terminal';
import Modules from 'shared_modules/Modules';
import Monitoring from 'shared_modules/Monitoring';
import { TERMINAL_TYPE } from 'shared_modules/Terminals';

const _MINIMUM_POLLING_INTERVAL = 3000; // Number of ms between polling interval
const _POLLING_BACKOFF_CONSTANT = 12;
export const _POLLING_WARNING_THRESHOLD = 2; // Number of polls before warning about degraded
export const _POLLING_ERROR_THRESHOLD = 15000; // Number of ms before we tell the merchant something is wrong after the warning happened

/**
 * Enum used by the CustomEvent for polling
 */
export const POLLING_EVENT = {
  NAME: 'onPollingStatusUpdate',
  STATUS: {
    DEGRADED: 'degraded',
    KO: 'ko',
    OK: 'ok',
  },
};

/**
 * Enum used by the monitoring
 */
export const EVENT_TYPES = {
  PAYMENT: {
    COMPLETED: 'payment.completed',
    STAGED: 'payment.staged',
    STAGING: 'payment.staging',
    STARTED: 'payment.started',
    TERMINAL: {
      DISCOVERING: 'payment.terminal.discovering',
      DISCOVERED: 'payment.terminal.discovered',
      CONNECTING: 'payment.terminal.connecting',
      CONNECTED: 'payment.terminal.connected',
      COLLECTING: 'payment.terminal.collecting',
      COLLECTED: 'payment.terminal.collected',
      PROCESSING: 'payment.terminal.processing',
      PROCESSED: 'payment.terminal.processed',
      CAPTURED: 'payment.terminal.captured',
    },
    POLLING: {
      [POLLING_EVENT.STATUS.DEGRADED]: 'payment.polling.degraded',
      [POLLING_EVENT.STATUS.KO]: 'payment.polling.ko',
    },
  },
};

const schemaP400 = {
  pTerminalId: 'id',
  pTerminalLocation: 'location',
  pTerminalIp: 'ip_address',
  pTerminalFirmware: 'device_sw_version',
};

export class TerminalEventMonitoring {
  constructor(ccTerminal, source) {
    this._source = source || null;
    this._uniqueClientTxId = uuid();
    this._gatewayConfigId = get(ccTerminal, 'gatewayConfigID', null);
    this._ccTerminalId = get(ccTerminal, 'ccTerminalID', null);
    this._terminalSerialNumber = get(ccTerminal, 'configuration.serialNumber', null);
    this._paymentId = null;
    this._paymentStatus = null;
    this._terminal = null;
    this._start = Date.now();
    this._last = this._start;

    // The following values can be 'unknown'
    this._terminalType = get(ccTerminal, 'type', 'unknown');
    this._terminalModelNumber = get(ccTerminal, 'configuration.modelNumber', 'unknown');
  }

  /**
   * getter paymentStatus
   */
  get paymentStatus() {
    return this._paymentStatus;
  }

  /**
   * setter paymentStatus
   *
   * @param {String} paymentStatus
   */
  set paymentStatus(paymentStatus) {
    this._paymentStatus = paymentStatus;
  }

  /**
   * getter terminal
   */
  get terminal() {
    return this._terminal;
  }

  /**
   * setter terminal
   *
   * @param {Object} terminalRaw data object from the terminal information
   */
  set terminal(terminalRaw) {
    switch (this._terminalType) {
      case TERMINAL_TYPE.LIGHTSPEED_P400_PLUS_STRIPE:
      case TERMINAL_TYPE.LIGHTSPEED_BBPOS_WISEPOSE_STRIPE:
      case TERMINAL_TYPE.LIGHTSPEED_STRIPE_READER_S700_STRIPE:
      case TERMINAL_TYPE.LIGHTSPEED_STRIPE_READER_S710_STRIPE:
        this._terminal = morphism(schemaP400, terminalRaw);
        break;
      default:
        this._terminal = {};
    }
  }

  /**
   * getter paymentId
   */
  get paymentId() {
    return this._paymentId;
  }

  /**
   * setter paymentId
   *
   * @param {String} paymentId
   */
  set paymentId(paymentId) {
    this._paymentId = paymentId;
  }

  /**
   * Abstract the addEvent monitoring from
   * the monitoring helper
   *
   * @param {String} eventName
   */
  submitEvent(eventName) {
    const now = Date.now();

    Monitoring.addEvent(eventName, this._source, {
      uniqueClientTxId: this._uniqueClientTxId,
      gatewayConfigId: this._gatewayConfigId,
      ccTerminalId: this._ccTerminalId,
      terminalType: this._terminalType,
      terminalModelNumber: this._terminalModelNumber,
      terminalSerialNumber: this._terminalSerialNumber,
      paymentId: this._paymentId,
      paymentStatus: this._paymentStatus,
      msSinceStart: now - this._start,
      msSinceLast: now - this._last,
      ...this._terminal,
    });

    this._last = now;
  }

  /**
   * Abstract the addError monitoring from
   * the monitoring helper
   *
   * @param {Error} error - Javascript Error object
   * @param {String} code - error code
   */
  submitError(error, code = null) {
    Monitoring.addError(error, this._source, {
      uniqueClientTxId: this._uniqueClientTxId,
      gatewayConfigId: this._gatewayConfigId,
      ccTerminalId: this._ccTerminalId,
      terminalType: this._terminalType,
      terminalModelNumber: this._terminalModelNumber,
      terminalSerialNumber: this._terminalSerialNumber,
      paymentId: this._paymentId,
      paymentStatus: this._paymentStatus,
      errorCode: code || error.code,
      reason: error.reason || null,
      ...this._terminal,
    });
  }
}

export default class PaymentServiceTerminal extends Terminal {
  constructor(terminalModel, pollingInterval = _MINIMUM_POLLING_INTERVAL) {
    super(terminalModel);

    this._pollingTimer = null;
    this._elapsedTime = 0;
    this._missedPollCount = 0;
    this._errorPollingTimeout = null;
    this.terminalEventMonitoring = null;
    /** @type {LspayTerminalMonitoring} */
    this.lspayMonitoring = null;
    this.pollingInterval = pollingInterval;
    this.hasMonitoringModule = Modules.hasModule('payments.terminal_monitoring');
  }

  get missedPollCount() {
    return this._missedPollCount;
  }

  set missedPollCount(count) {
    this._missedPollCount = count;
  }

  /**
   * Initializes LSPAY terminal monitoring and sends the start event.
   * @param {string} pli
   * @param {object} startEventAttributes
   */
  startLspayMonitoring(pli, startEventAttributes = {}) {
    const terminalModel = this.terminalModel ?? this._model;
    const terminalModelType = terminalModel?.attributes?.type;

    if (!this.lspayMonitoring) {
      const integrationType =
        terminalModelType === CCTerminal.TerminalType.LIGHTSPEED_BBPOS_WISEPOSE_STRIPE ||
        terminalModelType === CCTerminal.TerminalType.LIGHTSPEED_STRIPE_READER_S700_STRIPE ||
        terminalModelType === CCTerminal.TerminalType.LIGHTSPEED_STRIPE_READER_S710_STRIPE
          ? INTEGRATION_TYPE.CLOUD
          : INTEGRATION_TYPE.LOCAL;
      this._initLspayMonitoring(pli, integrationType);
    }

    this.lspayMonitoring.setTerminal({
      id: terminalModel?.id,
      serialNumber: terminalModel?.attributes?.configuration?.serialNumber,
      model:
        {
          [TERMINAL_TYPE.LIGHTSPEED_BBPOS_WISEPOSE_STRIPE]: TERMINAL_MODEL.BBPOS_WISEPOS_E,
          [TERMINAL_TYPE.LIGHTSPEED_P400_PLUS_STRIPE]: TERMINAL_MODEL.VERIFONE_P400,
          [TERMINAL_TYPE.LIGHTSPEED_BBPOS_CHIPPER]: TERMINAL_MODEL.BBPOS_CHIPPER2X,
          [TERMINAL_TYPE.LIGHTSPEED_BBPOS_WISEPAD_3_STRIPE]: TERMINAL_MODEL.BBPOS_WISEPAD3,
          [TERMINAL_TYPE.LIGHTSPEED_STRIPE_READER_M2_STRIPE]: TERMINAL_MODEL.STRIPE_M2,
          [TERMINAL_TYPE.LIGHTSPEED_STRIPE_READER_S700_STRIPE]: TERMINAL_MODEL.STRIPE_S700,
          [TERMINAL_TYPE.LIGHTSPEED_STRIPE_READER_S710_STRIPE]: TERMINAL_MODEL.STRIPE_S710,
        }[terminalModelType] ?? TERMINAL_MODEL.UNKNOWN,
    });

    this.lspayMonitoring.sendEvent(TERMINAL_EVENT.START, startEventAttributes);
  }

  _initLspayMonitoring(pli, integrationType) {
    const { hostname } = window.location;
    let env = 'TRIAL';
    if (/lightspeedapp(?:staging|stg)/.test(hostname)) {
      env = 'STAGE';
    } else if (/merchantos|lightspeedapp/.test(hostname)) {
      env = 'PROD';
    }

    this.lspayMonitoring = LspayTerminalMonitoring.init({
      clientToken: 'pub9dc1c44452c2a54cbc2fe1068a9b06ee',
      service: 'r-series-pos',
      env,
      product: Product.rSeries,
      pli,
      processor: PROCESSOR.STRIPE,
      platformType: PLATFORM_TYPE.WEB,
      verbose: env !== 'PROD',
      integrationType,
    });
  }

  /**
   * This method restore all the timers and the polling count
   */
  reset() {
    if (this.missedPollCount > 0) {
      this.missedPollCount = 0;
    }

    if (this._errorPollingTimeout) {
      // Reset and clear the timers
      clearTimeout(this._errorPollingTimeout);
      this._errorPollingTimeout = null;
    }

    if (this._pollingTimer) {
      // Reset and clear the timers
      clearTimeout(this._pollingTimer);
      this._pollingTimer = null;
    }
  }

  /**
   * Creates the appropriate payment transaction structure for a pending payment.  We'll use
   * that to attempt to recover a pending transaction.
   *
   * @param {Payment} payment The payment for which we want to attempt to continue
   * @param {GatewayConfig} gatewayConfig The gateway configuration used to transact the request
   */
  getRecoveredTransaction(payment, gatewayConfig) {
    // store the last transaction that was created; used to obtain details about the last
    // transaction that was initiated with this terminal
    this.lastPayment = payment;

    let terminalPayment = this._createPayment(payment.get('totalAmount'));
    let paymentTransaction = new PaymentTransaction({
      paymentTransactionID: payment.nextPaymentTransactionID,
      gateway: gatewayConfig.get('gatewayID'),
      paymentID: payment.id,
      shopID: CommonData.selectedShop.id,
      registerID: CommonData.selectedRegister.id,
      terminalID: this.model.id,
    });

    paymentTransaction.gatewayRequest = payment.terminalApiUrl;

    let salePayment = payment.getRelation('SalePayment', SalePayment);
    if (salePayment) {
      terminalPayment.model.set(salePayment.attributes);
    }

    return { payment, terminalPayment, paymentTransaction };
  }

  /**
   * Communicates with the terminal to start a transaction.
   *
   * @param {Object} paymentOptions
   * @param {Decimal} paymentOptions.amount  The amount to transact with the terminal.
   * @param {Decimal} paymentOptions.tipAmount  The tipAmount to transact with the terminal.
   * @param {Number} paymentOptions.saleID   The ID of the sale to which the transaction will be associated
   * @param {Number} paymentOptions.customerId  Optional - The customer ID associated with the sale
   * @param {GatewayConfig} paymentOptions.gatewayConfig  The gateway configuration used to transact the request
   * @param {Boolean} paymentOptions.isManualEntry  When true, the terminal will request for manual card entry
   * @param {Payment} paymentOptions.recoveredPayment Optional - When specified, the system will attempt to
   *                                                  continue the specified payment. This can occurr if there
   *                                                  is a pending payment in the sale that was unresolved.
   * @param {String} paymentOptions.refundedPaymentId Optional - When specified, the system will attempt to
   *                                                  perform a refund via the terminal (currently only Stripe)
   * @param {String} paymentOptions.workflow Optional - Override for the payment workflow
   * @param {String} paymentOptions.captureMethod Optional - The capture method (e.g., 'manual' or 'automatic')
   */
  stagePayment({
    amount,
    saleID,
    customerId = null,
    gatewayConfig,
    isManualEntry = false,
    tipAmount = null,
    recoveredPayment = null,
    refundedPaymentID = null,
    cardToken = null,
    workflow = Payment.Workflow.TERMINAL_PAYMENTS,
    integrationType = null,
    uniqueClientTxId = null,
    enableTerminalTipping = null,
    startTime = Date.now(),
    clientTime = Date.now(),
    enableSurcharge = null,
    captureMethod = null,
  }) {
    const shopID = CommonData.selectedShop.id;
    const registerID = CommonData.selectedRegister.id;

    if (shopID && registerID) {
      let deferred = $.Deferred();
      if (saleID && gatewayConfig?.get('gatewayID')) {
        if (recoveredPayment) {
          deferred.resolve(this.getRecoveredTransaction(recoveredPayment, gatewayConfig));
        } else {
          let payment = new Payment({
            method: Payment.Methods.BANK_CARD,
            totalAmount: amount,
            saleID,
            // TODO: Eventually the payment service will take care of setting the payment type
            // but for now just set it to the credit card type
            paymentTypeID: PaymentTypeManager.getPaymentIdForType(
              PaymentTypeManager.Types.TERMINAL
            ),
            shopID,
            registerID,
            ...(tipAmount && { tipAmount }),
          });

          payment.gateway = gatewayConfig.get('gatewayID');
          payment.terminalConfigID = this.model.id;
          payment.uniqueClientTxId = uniqueClientTxId;
          payment.workflow = workflow;
          payment.isManualEntryAllowed = isManualEntry;
          payment.refundedPaymentId = refundedPaymentID;
          payment.refundCardToken = cardToken;
          payment.integrationType = integrationType;

          if (Modules.hasModule('payments.pre_auth') && customerId) {
            payment.customerId = customerId;
          }

          const isTerminalTippingEnabled = Modules.hasModule('payments.terminal_tipping') ?? false;
          if (isTerminalTippingEnabled) {
            payment.enableTerminalTipping = enableTerminalTipping;
          }

          const isSurchargeEnabled = Modules.hasModule('payments.surcharge') ?? false;
          if (isSurchargeEnabled) {
            payment.enableSurcharge = enableSurcharge;
          }

          if (Modules.hasModule('payments.pre_auth') && captureMethod) {
            payment.captureMethod = captureMethod;
          }

          if (Modules.hasModule('payments.monitoring.start.time')) {
            payment.clientTime = clientTime;
            payment.startTime = startTime;
          }

          if (isManualEntry) {
            // put the terminal (when available) into manual entry mode
            payment.invokeManualEntry = true;
          }

          // POST on the payment obtains the forged URL to initiate the transaction and creates
          // a SalePayment against the sale.
          payment
            .save()
            .done(() => {
              // store the last transaction that was created; used to obtain details about the last
              // transaction that was initiated with this terminal
              this.lastPayment = payment;

              let terminalPayment = this._createPayment(amount);
              let paymentTransaction = new PaymentTransaction({
                paymentTransactionID: payment.nextPaymentTransactionID,
                gateway: gatewayConfig.get('gatewayID'),
                paymentID: payment.id,
                shopID: CommonData.selectedShop.id,
                registerID: CommonData.selectedRegister.id,
                terminalID: this.model.id,
              });

              paymentTransaction.gatewayRequest = payment.terminalApiUrl;

              let salePayment = payment.getRelation('SalePayment', SalePayment);
              if (salePayment) {
                terminalPayment.model.set(salePayment.attributes);
              }

              deferred.resolve({ payment, terminalPayment, paymentTransaction });
            })
            .fail((jqXHR, textStatus, errorThrown) => {
              this.lspayMonitoring?.setAttributes({
                paymentSaveError: { jqXHR, textStatus, errorThrown },
              });

              // pass along the API-reported error without the file and line number
              let error = get(jqXHR, 'responseJSON.message', '').replace(/ *\([^)]*\) */g, '');

              new TerminalEventMonitoring(
                this.model.attributes,
                'PaymentServiceTerminal'
              ).submitError(new Error(error));

              deferred.reject(
                new TerminalTransactionResponse(
                  Terminal.Response.ERROR,
                  Terminal.Status.ERROR,
                  null,
                  error
                )
              );
            });
        }
      } else {
        const message = `A sale ID, gateway ID and terminalConfigID is required to process a payment for a Payment Service Terminal`;

        new TerminalEventMonitoring(this.model.attributes, 'PaymentServiceTerminal').submitError(
          new Error(message)
        );

        return deferred.reject(
          new TerminalTransactionResponse(
            Terminal.Response.ERROR,
            Terminal.Status.ERROR,
            null,
            message
          )
        );
      }

      return deferred.promise();
    }

    flashNotifier('warning', Localize.translate('There is a issue with your Shop Setup'), 4);
  }

  /**
   * Polls the status of the specified payment at a regular interval.  Once the status is no longer
   * pending the promise will be resolved or rejected accordingly.
   *
   * @param {Payment} payment     The payment who's status is currently unknown
   * @returns {*}
   */
  pollApiForPaymentStatus(payment) {
    let deferred = $.Deferred();

    this._elapsedTime = 0;
    if (this._pollingTimer) {
      clearTimeout(this._pollingTimer);
    }

    this._getPaymentStatus(payment, deferred);

    return deferred.promise();
  }

  /**
   * Generates the duration before the next poll for a payment transaction status.  Delegate method
   * invoked by the pollApiForPaymentStatus() method.
   *
   * @param {Number} elapsedTime      The elapsed time in milliseconds
   */
  nextPollingInterval(elapsedTime = 0) {
    // time to next retry = max(3, elapsed time in seconds / 12) seconds
    return Math.max(this.pollingInterval, elapsedTime / _POLLING_BACKOFF_CONSTANT);
  }

  /**
   * Dispatches the proper event when the different thresholds
   * _POLLING_WARNING_THRESHOLD and _POLLING_ERROR_THRESHOLD
   * are reached.
   */
  badPoll() {
    if (this.missedPollCount <= _POLLING_WARNING_THRESHOLD) {
      // eslint-disable-next-line operator-assignment
      this.missedPollCount = this.missedPollCount + 1;
    }

    if (this.missedPollCount === _POLLING_WARNING_THRESHOLD) {
      Monitoring.addError(
        EVENT_TYPES.PAYMENT.POLLING[POLLING_EVENT.STATUS.DEGRADED],
        'PaymentServiceTerminal'
      );
      window.dispatchEvent(
        new CustomEvent(POLLING_EVENT.NAME, { detail: POLLING_EVENT.STATUS.DEGRADED })
      );

      if (!this._errorPollingTimeout) {
        this._errorPollingTimeout = setTimeout(() => {
          Monitoring.addError(
            EVENT_TYPES.PAYMENT.POLLING[POLLING_EVENT.STATUS.KO],
            'PaymentServiceTerminal'
          );
          window.dispatchEvent(
            new CustomEvent(POLLING_EVENT.NAME, { detail: POLLING_EVENT.STATUS.KO })
          );

          clearTimeout(this._errorPollingTimeout);
        }, _POLLING_ERROR_THRESHOLD);
      }
    }
  }

  /**
   * Dispatches the proper event when receive a good
   * poll after a series of bad ones. It also reset the
   * missedPollCount to 0
   */
  goodPoll() {
    // Reset the missed poll count since this query worked
    if (this.missedPollCount > 0) {
      this.missedPollCount = 0;

      if (this._errorPollingTimeout) {
        // Reset and clear the timers
        clearTimeout(this._errorPollingTimeout);
        this._errorPollingTimeout = null;
      }

      window.dispatchEvent(
        new CustomEvent(POLLING_EVENT.NAME, { detail: POLLING_EVENT.STATUS.OK })
      );
    }
  }

  /**
   * Initiate the setTimeout for the polling that will calls
   * the update of the payment status at every nextPollingInterval
   *
   * @param {Payment} payment
   * @param {JQueryDeferred} deferred promise
   */
  _keepPolling(payment, deferred) {
    // We opt to use setTimeout rather than setInterval since we
    // want to avoid the potential case where the fetch from the API takes longer
    // than the polling interval.
    if (this._pollingTimer) {
      clearTimeout(this._pollingTimer);
    }

    this._pollingTimer = setTimeout(() => {
      this._elapsedTime += this.nextPollingInterval(this._elapsedTime);
      this._getPaymentStatus(payment, deferred);
    }, this.nextPollingInterval(this._elapsedTime));
  }

  /**
   * Helper function to retrieve the status of the payment from the API.  Once the status is no longer
   * pending the promise will be resolved or rejected accordingly.
   * @param {Payment} payment     The payment whose status is currently unknown
   * @param {Deferred} deferred   The deferred object that _pollApiForPaymentStatus uses to determine
   *                              when the status of the payment has been resolved.
   * @private
   */
  _getPaymentStatus(payment, deferred) {
    payment
      .fetch()
      .done(() => {
        this.goodPoll();

        switch (payment.get('status')) {
          case Payment.Status.APPROVED:
            deferred.resolve();
            delete this._pollingTimer;
            break;

          case Payment.Status.PENDING:
            // Keep polling.  We opt to use setTimeout rather than setInterval since we
            // want to avoid the potential case where the fetch from the API takes longer
            // than the polling interval.
            this._keepPolling(payment, deferred);
            break;

          default:
            deferred.reject();
            delete this._pollingTimer;
            break;
        }
      })
      .fail(() => {
        this.badPoll();
        this._keepPolling(payment, deferred);
      });
  }

  /**
   * Cancels payment at the Pay-Service level
   *
   * @param {Payment|undefined} payment The payment to cancel, may not exist under certain conditions,
   * e.g. unreferenced refunds (but we still want to dispatch the CANCLED_STARTED event)
   * @returns a Promise that resolves to an empty object once the command has been successfully canceled.
   * @return {error} If cancellation fails, the Promise resolves to an object with an error
   */
  async cancelPayment(payment) {
    const event = new CustomEvent(Terminal.TerminalEvents.CANCEL_STARTED, {
      detail: { disableCancelButton: true },
    });
    window.dispatchEvent(event);

    try {
      if (payment) {
        await payment.cancelPayment();
      }
    } catch (error) {
      this._cancelPaymentErrorToTerminalTransactionResponse(error);
    }
  }

  _cancelPaymentErrorToTerminalTransactionResponse(error) {
    switch (get(error, 'response.status', undefined)) {
      case 424:
        throw new TerminalTransactionResponse(
          Terminal.Response.CANCEL_UNAVAILABLE,
          Terminal.Status.ERROR,
          null,
          error
        );
      case 409:
        throw new TerminalTransactionResponse(
          Terminal.Response.NON_CANCELLABLE_STATE,
          Terminal.Status.ERROR,
          null,
          error
        );
      default:
        throw new Error(
          `Something went wrong while trying to abort the payment. If the problem persists please contact support.`
        );
    }
  }
}
